import {FormConfig} from '../../form/form.config';
import {FormModelText} from '../../form/form.model.text';
import {FormModel} from '../../form/form.model.abstract';
import {Validators} from '@angular/forms';
import {FormModelRadio} from '../../form/form.model.radio';
import {ValidationService} from '../../services/validation.service';
import {FormModelHidden} from '../../form/form.model.hidden';
import {FormModelNumber} from '../../form/form.model.number';
import {StromcheckAnfrage} from '../anfrage/stromcheck.anfrage';
import {StromcheckConstants} from '../stromcheck.constants';
import {FormModelDates} from '../../form/form.model.dates';
import {StepComponent} from "../../step/step.component";

/**
 * Configuration for the input of the first chapter in the stromcheck advisor.
 * @inheritDoc
 */
export class EingabeForm extends FormConfig {

  /**
   * (input) data of the user
   */
  stepComponent: StepComponent;

  /**
   * some data is needed here for validation
   */
  public anfrage: StromcheckAnfrage;

  /**
   * @inheritDoc
   *
   * ATTENTION: Some of these values/fields are also used in StromcheckController.php. Check changes also there.
   *
   * @protected
   */
  protected fields: FormModel[] = [
    // Basisinformationen
    new FormModelRadio('gebaeudetyp', ['1-2familienhaus', 'mehrfamilienhaus'], [Validators.required], null, null, false),
    (new FormModelText('postleitzahl', [Validators.required, ValidationService.postalCode], null, null, true)).setClasses('small-field'),
    new FormModelHidden('ort'),
    new FormModelNumber('bewohneranzahl', [Validators.required, Validators.min(1), Validators.max(StromcheckConstants.FORM_PEOPLE_MAX), ValidationService.allowIntegerOnly],
      null, StromcheckConstants.FORM_PEOPLE_DEFAULT, false),
    new FormModelRadio('warmwasser_ueber_strom', ['ja', 'nein'], [Validators.required], null, null, false),
    new FormModelRadio('nutzung_oekostrom', ['ja', 'nein', 'unbekannt'], [Validators.required], null, null, false),
    new FormModelRadio('photovoltaik_vorhanden', ['ja', 'nein'], [Validators.required], null, null, false),
    (new FormModelRadio('photovoltaik_stromspeicher_vorhanden', ['ja', 'nein'], [Validators.required], null, null, false)).setInactive(),
    (new FormModelText('photovoltaik_stromspeicher_menge',  [Validators.required, ValidationService.greaterZero], 'kwh', null, false, 'number')).setInactive(),
    new FormModelRadio('waermepumpe_vorhanden', ['ja', 'nein'], [Validators.required], null,null, false),
    (new FormModelRadio('waermepumpe_stromverbrauch_bekannt', ['ja', 'nein'], [Validators.required], null,null, false)).setInactive(),
    (new FormModelRadio('waermepumpe_zweitarifzaehler', ['ja', 'nein', 'unbekannt'], [Validators.required], null,null, false)).setInactive(),

    // Verbrauchsdaten
    new FormModelDates('abrechnungszeitraum', ['abrechnung_von', 'abrechnung_bis'], [Validators.required, ValidationService.dateFormat], null, [FormConfig.getFirstDayOfLastYear(), FormConfig.getLastDayOfLastYear()], true),
    new FormModelText('strom_verbrauch_kwh', [Validators.required, ValidationService.greaterZero], 'kwh', null, true, 'number'),
    new FormModelText('photovoltaik_gesamtertrag', [Validators.required, ValidationService.greaterZero], 'kwh', null, true, 'number'),
    new FormModelText('photovoltaik_einspeisung', [Validators.required, ValidationService.greaterZero], 'kwh', null, true, 'number'),
    (new FormModelText('waermepumpe_stromverbrauch', [], 'kwh',0, false, 'number')).setInactive(),
    new FormModelText('strom_kosten', [Validators.required, ValidationService.greaterZero], 'eur', null, false, 'number'),

    // Ergebnisseite
    new FormModelRadio('elektrofahrzeug_vorhanden', ['ja', 'nein'], [Validators.required], null, null, false),
    new FormModelRadio('ladestrom_elektrofahrzeug_bekannt', ['ja', 'nein'], [Validators.required], null, null, false),
    new FormModelText('ladestrom_elektrofahrzeug_verbrauch', [ValidationService.greaterZero], 'kwh', null, false, 'number'),
    new FormModelText('grundpreis_euro_monat', [Validators.required, ValidationService.greaterZero], 'euro_monat', null, false, 'number'),
    new FormModelText('arbeitspreis_cent_kwh', [Validators.required, ValidationService.greaterZero], 'cent_kwh', null, false, 'number')
  ];

  /**
   * @inheritDoc
   * @protected
   */
  protected formValidators = [
    ValidationService.dateRangeMinMax(
      'abrechnung_von', 'abrechnung_bis', StromcheckConstants.INVOICE_MIN_DAYS, StromcheckConstants.INVOICE_MAX_DAYS
    ),
    ValidationService.smallerEqual('photovoltaik_einspeisung', 'photovoltaik_gesamtertrag')
  ];
  /**
   * List of formfields where changes of the value, change other fields.
   */
  fieldsForFormChanges: string[] = [
    'gebaeudetyp',
    'photovoltaik_vorhanden',
    'photovoltaik_stromspeicher_vorhanden',
    'waermepumpe_vorhanden'
  ];

  /**
   * @inheritDoc
   * @protected
   */
  protected groups = {
    'kurz': ['gebaeudetyp'],
    'kopfbuehne': ['gebaeudetyp'],
    'basisinformationen/1': ['gebaeudetyp'],
    'basisinformationen/2': ['postleitzahl', 'ort'],
    'basisinformationen/3': ['bewohneranzahl'],
    'basisinformationen/4': ['warmwasser_ueber_strom'],
    'basisinformationen/5': ['nutzung_oekostrom'],
    'basisinformationen/6': ['photovoltaik_vorhanden', 'photovoltaik_stromspeicher_vorhanden', 'photovoltaik_stromspeicher_menge'],
    'basisinformationen/7': ['waermepumpe_vorhanden', 'waermepumpe_stromverbrauch_bekannt', 'waermepumpe_zweitarifzaehler'],
    'verbrauchsdaten': ['abrechnungszeitraum', 'strom_verbrauch_kwh', 'photovoltaik_gesamtertrag', 'photovoltaik_einspeisung', 'waermepumpe_stromverbrauch']
  };

  /**
   * Sets stepComponent
   * @param stepComponent
   */
  setStepComponent(stepComponent: StepComponent): void {
    this.stepComponent = stepComponent;
  }

  /**
   * Any time a field is updated, this method is called.
   * @param values
   */
  updateFields(values: { [key: string]: any }): any {
    // changes based on field 'photovoltaik_vorhanden'
    if (values.hasOwnProperty('photovoltaik_vorhanden')) {
      if (values.photovoltaik_vorhanden === 'ja') {
        this.activateField('photovoltaik_gesamtertrag');
        this.activateField('photovoltaik_einspeisung');
        this.activateField('photovoltaik_stromspeicher_vorhanden');
        if(values.hasOwnProperty('photovoltaik_stromspeicher_vorhanden')) {
          if(values.photovoltaik_stromspeicher_vorhanden === 'ja') {
            this.activateField('photovoltaik_stromspeicher_menge');
          }
          else {
            this.deactivateField('photovoltaik_stromspeicher_menge');
            values.photovoltaik_stromspeicher_menge = null;

          }
        }
      }
      else {
        this.deactivateField('photovoltaik_gesamtertrag');
        this.deactivateField('photovoltaik_einspeisung');
        this.deactivateField('photovoltaik_stromspeicher_vorhanden');
        this.deactivateField('photovoltaik_stromspeicher_menge');
        values.photovoltaik_gesamtertrag = null;
        values.photovoltaik_einspeisung = null;
        values.photovoltaik_stromspeicher_vorhanden = null;
        values.photovoltaik_stromspeicher_menge = null;
      }
    }

    //changes based on gebauedetyp
    if (values.hasOwnProperty('gebaeudetyp')) {
      if (values.gebaeudetyp === 'mehrfamilienhaus') {
        this.deactivateField('waermepumpe_vorhanden');
      }
      else {
        this.activateField('waermepumpe_vorhanden');
      }
    }

    //changes based on waermepumpe_vorhanden
    if (values.hasOwnProperty('waermepumpe_vorhanden')) {
      if (values.waermepumpe_vorhanden === 'ja') {
        this.activateField('waermepumpe_stromverbrauch_bekannt');
        this.activateField('waermepumpe_zweitarifzaehler');
        if (values.waermepumpe_stromverbrauch_bekannt === 'ja') {
          this.activateField('waermepumpe_stromverbrauch');
        }
        else {
          this.deactivateField('waermepumpe_stromverbrauch');
          values.waermepumpe_stromverbrauch = null;
        }
      }
      else {
        this.deactivateField('waermepumpe_stromverbrauch_bekannt');
        this.deactivateField('waermepumpe_zweitarifzaehler');
        values.waermepumpe_stromverbrauch_bekannt = null;
        values.waermepumpe_zweitarifzaehler = null;
        values.waermepumpe_stromverbrauch = null;
      }
    }

    return values;
  }

  /**
   * In this function all dynamic changes of the form is done.
   * Based on selection options or value other fields are shown/hidden, options changed and validators adapted.
   *
   * @inheritDoc
   * @param values
   * @protected
   */
  protected onSetData(values: { [key: string]: any }): void {
    this.updateFields(values);
  }

  /**
   * @inheritDoc
   * @protected
   */
  protected onChange() {
    // check if the form has some fields which value changes other fields.
    if (this.fieldsForFormChanges.find(field => this.form.get(field)) !== undefined) {
      const newValues = this.updateFields(this.getData());
      if(this.stepComponent) {
        this.stepComponent.saveData(newValues);
      }
    }
  }

}
